import axiosInstance, { endpoints } from 'src/lib/axios';

import {
  bokfördaArbetsgivardeklarationer,
  inlämnadeMomsdeklarationer,
  inlämnadeInkomstdeklarationer,
  genomfördaStämmor,
} from './avklarat-mappning';
import { hämtaArkiv } from 'src/sections/styrelse/StyrelseService';
import { medOrgHuvud } from 'src/lib/org-huvud';

/**
 * Uppgifter som redan är utförda enligt appens egen data.
 *
 * En arbetsgivardeklaration, momsdeklaration, inkomstdeklaration eller stämma
 * som är bokförd, inlämnad respektive genomförd behöver inte bockas av för hand
 * — den är bevisligen gjord.
 *
 * Själva nyckelmappningen ligger i `avklarat-mappning.ts` och är testad där.
 */

export type Avklarat = {
  /** Uppgifts-id → varför den räknas som klar. Nyckeln matchar `Myndighetsuppgift.id`. */
  anledningPerUppgift: Record<string, string>;
  /**
   * Om organisationen har några arbetsgivardeklarationer inlästa alls, bokförda
   * eller ej. Signal till `bedömArbetsgivare` om att organisationen faktiskt är
   * arbetsgivare. undefined när hämtningen inte gick igenom.
   */
  harArbetsgivardeklarationer?: boolean;
};

export const INGET_AVKLARAT: Avklarat = { anledningPerUppgift: {} };

/**
 * Markerar en källa som inte frågades ut. `Promise.allSettled` ger då `rejected`, vilket
 * faller ut på samma gren som ett fel — och det är rätt: i båda fallen vet vi inte om
 * uppgiften är gjord, till skillnad från att veta att den inte är det.
 */
const AVSTÄNGD = new Error('Källan är avstängd för den här organisationen');

type AgdResultat = { avklarat: Record<string, string>; finnsNågon: boolean };

async function hämtaArbetsgivardeklarationer(orgId: string): Promise<AgdResultat> {
  const svar = await axiosInstance.get(`${endpoints.skatt.arbetsgivardeklarationer.root}/${orgId}`, medOrgHuvud(orgId));
  const lista = svar?.data?.arbetsgivardeklarationer;
  return {
    avklarat: bokfördaArbetsgivardeklarationer(lista),
    finnsNågon: Array.isArray(lista) && lista.length > 0,
  };
}

async function hämtaMomsdeklarationer(orgId: string): Promise<Record<string, string>> {
  const svar = await axiosInstance.get(endpoints.momsdeklaration.hämta(orgId), medOrgHuvud(orgId));
  return inlämnadeMomsdeklarationer(svar?.data);
}

async function hämtaInkomstdeklarationer(orgId: string): Promise<Record<string, string>> {
  const svar = await axiosInstance.get(endpoints.skatt.inkomstdeklarationer.hämta(orgId), medOrgHuvud(orgId));
  return inlämnadeInkomstdeklarationer(svar?.data);
}

async function hämtaStämmor(orgId: string): Promise<Record<string, string>> {
  const svar = await hämtaArkiv(orgId);
  return genomfördaStämmor(svar);
}

/** Vilka källor som får frågas ut. Se {@link ../att-gora/sonder} för varför. */
export type AvklaratKällor = {
  /** Arbetsgivar-, moms- och inkomstdeklarationer. Menynyckeln `skatt`. */
  skatt: boolean;
  /** Genomförda stämmor ur styrelsearkivet. Menynyckeln `styrelse`. */
  styrelse: boolean;
};

/** Alla källor. Standard, så att en anropare som inte bryr sig beter sig som förut. */
export const ALLA_KÄLLOR: AvklaratKällor = { skatt: true, styrelse: true };

/**
 * Hämtar de källor som är påslagna. En källa som fallerar tar inte de andra med sig, och
 * ett totalfel ger tom avbockning — uppgiften visas då som öppen, vilket är
 * rätt sida att fela på för en påminnelselista.
 *
 * <p>En avstängd källa ger inget anrop alls. Det är hela poängen: skatteytorna har egen
 * rate limiting och svarade 429 när listan frågade ut dem vid varje sidladdning, för
 * organisationer som inte ens har modulen påslagen.
 */
export async function hämtaAvklarat(
  orgId: string,
  källor: AvklaratKällor = ALLA_KÄLLOR
): Promise<Avklarat> {
  const [agd, moms, ink, stammor] = await Promise.allSettled([
    källor.skatt ? hämtaArbetsgivardeklarationer(orgId) : Promise.reject(AVSTÄNGD),
    källor.skatt ? hämtaMomsdeklarationer(orgId) : Promise.reject(AVSTÄNGD),
    källor.skatt ? hämtaInkomstdeklarationer(orgId) : Promise.reject(AVSTÄNGD),
    källor.styrelse ? hämtaStämmor(orgId) : Promise.reject(AVSTÄNGD),
  ]);

  return {
    anledningPerUppgift: {
      ...(agd.status === 'fulfilled' ? agd.value.avklarat : {}),
      ...(moms.status === 'fulfilled' ? moms.value : {}),
      ...(ink.status === 'fulfilled' ? ink.value : {}),
      ...(stammor.status === 'fulfilled' ? stammor.value : {}),
    },
    harArbetsgivardeklarationer: agd.status === 'fulfilled' ? agd.value.finnsNågon : undefined,
  };
}
